
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Trip } from '../types';
import { toast } from 'sonner';

interface UseThreadInitializationResult {
  threadId: string | null;
  isInitializing: boolean;
  connectionError: string | null;
  initializeChat: () => Promise<void>;
  handleRetry: () => void;
}

export const useThreadInitialization = (trip: Trip | null): UseThreadInitializationResult => {
  const [threadId, setThreadId] = useState<string | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [initializationAttempts, setInitializationAttempts] = useState(0);

  // Look for existing thread ID in local storage
  useEffect(() => {
    if (trip?.id) {
      const savedThreadId = localStorage.getItem(`tripAssistantThread_${trip.id}`);
      if (savedThreadId) {
        setThreadId(savedThreadId);
        setIsInitializing(false);
        return;
      }
    }
  }, [trip?.id]);

  const initializeChat = async () => {
    if (!trip) return;
    
    // If threadId is already set, no need to reinitialize
    if (threadId) {
      setIsInitializing(false);
      return;
    }
    
    setIsInitializing(true);
    setConnectionError(null);
    console.log('Initializing chat for destination:', trip.destination);
    
    try {
      const { data, error } = await supabase.functions.invoke('travel-assistant', {
        body: { 
          action: 'createThread',
          destination: trip.destination 
        }
      });
      
      if (error) {
        console.error('Error invoking travel-assistant function:', error);
        throw error;
      }
      
      if (!data?.threadId) {
        throw new Error('No thread ID received from server');
      }

      console.log('Chat thread created:', data.threadId);
      setThreadId(data.threadId);
      
      // Save thread ID to local storage for persistence
      if (trip.id) {
        localStorage.setItem(`tripAssistantThread_${trip.id}`, data.threadId);
      }
      
      setConnectionError(null);
    } catch (error) {
      console.error('Error initializing chat:', error);
      setConnectionError('Failed to initialize chat assistant. Please try again.');
    } finally {
      setIsInitializing(false);
    }
  };

  useEffect(() => {
    if (trip && !threadId && initializationAttempts < 3) {
      initializeChat();
      setInitializationAttempts(prev => prev + 1);
    }
  }, [trip, threadId]);

  const handleRetry = () => {
    setConnectionError(null);
    setIsInitializing(true);
    setInitializationAttempts(0);
    initializeChat();
  };

  return {
    threadId,
    isInitializing,
    connectionError,
    initializeChat,
    handleRetry
  };
};
